import { createContext, useContext, useMemo, type CSSProperties, type ReactElement } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';

const DEFAULT_TOP_OFFSET_PX = 30;
// No exact design token exists for the two colors below (closest border token
// is borders.default #CACACA, and text.primary is not pure black) — snapping
// would change the rendered design. Awaiting dedicated tokens; see CONV-6.
const BOXED_BORDER_COLOR = '#D2D2D2';
const BOXED_BORDER_RADIUS = '10px';
const BOXED_PADDING = '15px 40px';
const BOXED_GAP = '14px';
const BOXED_LOCK_COLOR = '#000000';

const InteractionMessageCompletionContext = createContext(false);

export const InteractionMessageCompletionProvider = InteractionMessageCompletionContext.Provider;

export type InteractionMessageVariant = 'plain' | 'boxed';

export interface InteractionMessageProps {
  requireInteraction: boolean;
  isComplete: boolean;
  message?: string;
  fallbackMessage?: string;
  variant?: InteractionMessageVariant;
  topOffset?: number;
  onClick?: () => void;
  ariaLabel?: string;
}

export function InteractionMessage({
  requireInteraction,
  isComplete,
  message,
  fallbackMessage,
  variant = 'plain',
  topOffset = DEFAULT_TOP_OFFSET_PX,
  onClick,
  ariaLabel,
}: InteractionMessageProps): ReactElement | null {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isCompletedByParentCanvas = useContext(InteractionMessageCompletionContext);

  const fontSize = isMobile
    ? tokens.typography.mobile.body.size
    : tokens.typography.body.size;
  const fontWeight = isMobile
    ? tokens.typography.mobile.body.weight
    : tokens.typography.body.weight;
  const lineHeight = isMobile
    ? tokens.typography.mobile.body.lineHeight
    : tokens.typography.body.lineHeight;

  const plainStyle = useMemo<CSSProperties>(
    () => ({
      margin: `${topOffset}px 0 0`,
      fontSize,
      fontWeight,
      lineHeight,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
      textAlign: 'center',
    }),
    [topOffset, fontSize, fontWeight, lineHeight, tokens.typography.fontFamily, tokens.text.primary]
  );

  if (!requireInteraction || isComplete || isCompletedByParentCanvas) {
    return null;
  }

  const resolvedMessage = message?.trim() || fallbackMessage || t('interaction.complete_to_continue');

  if (variant === 'boxed') {
    const wrapperStyle: CSSProperties = {
      marginTop: `${topOffset}px`,
      display: 'flex',
      justifyContent: 'center',
    };

    const boxStyle: CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: BOXED_GAP,
      padding: BOXED_PADDING,
      border: `1px solid ${BOXED_BORDER_COLOR}`,
      borderRadius: BOXED_BORDER_RADIUS,
      backgroundColor: 'transparent',
      ...(onClick
        ? {
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
          }
        : null),
    };

    const textStyle: CSSProperties = {
      margin: 0,
      fontSize,
      fontWeight,
      lineHeight,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.primary,
    };

    const boxContent = (
      <>
        <LockIcon color={BOXED_LOCK_COLOR} />
        <p style={textStyle}>{resolvedMessage}</p>
      </>
    );

    return (
      <div style={wrapperStyle}>
        {onClick ? (
          <button
            type="button"
            data-testid="interaction-message"
            style={boxStyle}
            onClick={onClick}
            aria-label={ariaLabel}
          >
            {boxContent}
          </button>
        ) : (
          <div data-testid="interaction-message" style={boxStyle}>
            {boxContent}
          </div>
        )}
      </div>
    );
  }

  return (
    <p data-testid="interaction-message" style={plainStyle}>
      {resolvedMessage}
    </p>
  );
}

function LockIcon({ color }: { color: string }): ReactElement {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="16"
      height="23"
      viewBox="0 0 16 23"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M5.33333 5.41176V8.11765H10.6667V5.41176C10.6667 3.9193 9.47083 2.70588 8 2.70588C6.52917 2.70588 5.33333 3.9193 5.33333 5.41176ZM2.66667 8.11765V5.41176C2.66667 2.42261 5.05417 0 8 0C10.9458 0 13.3333 2.42261 13.3333 5.41176V8.11765C14.8042 8.11765 16 9.33107 16 10.8235V20.2941C16 21.7866 14.8042 23 13.3333 23H2.66667C1.19583 23 0 21.7866 0 20.2941V10.8235C0 9.33107 1.19583 8.11765 2.66667 8.11765Z"
        fill={color}
      />
    </svg>
  );
}

InteractionMessage.displayName = 'InteractionMessage';
